import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useUpdateSubscriptionMutation } from 'src/apiClients';
import type { Subscription } from 'src/apiClients/queries';

import { useSectionEditState } from '../hooks/use-section-edit-state';
import { i18n } from '../InstanceDetailPage.i18n';
import { buildInstanceDetailSchema } from '../schema';
import type { InstanceDetailFormValues } from '../schema';
import { ConfirmDiscardDialog } from './confirm-discard-dialog/ConfirmDiscardDialog';
import { DetailSection } from './detail-section/DetailSection';
import { InfoField } from './info-field/InfoField';
import { useStyles } from './InstanceDetailForm.styles';
import { PdsLinkButton } from './pds-link-button/PdsLinkButton';
import { SalesforceLinkButton } from './salesforce-link-button/SalesforceLinkButton';

interface InstanceDetailFormProps {
    subscription: Subscription;
}

const toFormValues = (subscription: Subscription): InstanceDetailFormValues => ({
    subscriptionName: subscription.subscriptionName,
    comment: subscription.comment,
    salesforceLink: subscription.salesforceLink,
    customerName: subscription.customerName,
    companyName: subscription.companyName,
    capacityGroupId: subscription.capacityGroupId,
    capacityGroupName: subscription.capacityGroupName,
});

const trimmed = (value: string | undefined | null) => (value ?? '').trim();

export const InstanceDetailForm = ({ subscription }: InstanceDetailFormProps) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [updateSubscription, { isLoading: isSaving }] = useUpdateSubscriptionMutation();
    const editState = useSectionEditState();

    const schema = useMemo(
        () =>
            buildInstanceDetailSchema(
                intl.formatMessage(i18n.displayNameRequired),
                intl.formatMessage(i18n.capacityGroupIdInvalid),
            ),
        [intl],
    );

    const initialValues = useMemo(() => toFormValues(subscription), [subscription]);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isDirty, isValid },
    } = useForm<InstanceDetailFormValues>({
        resolver: zodResolver(schema),
        values: initialValues,
        mode: 'onChange',
    });

    const onSubmit = handleSubmit(async (values) => {
        try {
            await updateSubscription({ id: subscription.id, ...values }).unwrap();
            enqueueSnackbar(intl.formatMessage(i18n.saveSuccess), { variant: 'success' });
            editState.closeAfterSave();
        } catch {
            enqueueSnackbar(intl.formatMessage(i18n.saveError), { variant: 'error' });
        }
    });

    const handleConfirmDiscard = () => {
        reset(initialValues);
        editState.confirmDiscard();
    };

    return (
        <>
            <DetailSection
                automationId="instance-details"
                edit={{
                    isEditing: editState.isEditing,
                    canSave: isDirty && isValid,
                    isSaving,
                    onEdit: editState.enter,
                    onCancel: () => editState.requestExit(isDirty),
                    onSave: async () => {
                        await onSubmit();
                    },
                    editLabel: intl.formatMessage(i18n.edit),
                    cancelLabel: intl.formatMessage(i18n.cancel),
                    saveLabel: intl.formatMessage(i18n.save),
                }}
            >
                {editState.isEditing ? (
                    <Box
                        component="form"
                        className={classes.fields}
                        onSubmit={onSubmit}
                        automation-id="instance-detail-form"
                        noValidate
                    >
                        <TextField
                            {...register('subscriptionName')}
                            label={intl.formatMessage(i18n.displayName)}
                            error={!!errors.subscriptionName}
                            helperText={errors.subscriptionName?.message}
                            fullWidth
                            required
                            autoFocus
                            automation-id="field-display-name"
                        />
                        <TextField
                            {...register('customerName')}
                            label={intl.formatMessage(i18n.accountName)}
                            fullWidth
                            automation-id="field-account-name"
                        />
                        <TextField
                            {...register('companyName')}
                            label={intl.formatMessage(i18n.legalName)}
                            fullWidth
                            automation-id="field-legal-name"
                        />
                        <TextField
                            {...register('salesforceLink')}
                            label={intl.formatMessage(i18n.salesforceId)}
                            fullWidth
                            automation-id="field-salesforce-link"
                        />
                        <TextField
                            {...register('capacityGroupName')}
                            label={intl.formatMessage(i18n.capacityGroupName)}
                            fullWidth
                            automation-id="field-capacity-group-name"
                        />
                        <TextField
                            {...register('capacityGroupId')}
                            label={intl.formatMessage(i18n.capacityGroupId)}
                            error={!!errors.capacityGroupId}
                            helperText={errors.capacityGroupId?.message}
                            fullWidth
                            automation-id="field-capacity-group-id"
                        />
                        <TextField
                            {...register('comment')}
                            label={intl.formatMessage(i18n.comment)}
                            fullWidth
                            automation-id="field-comment"
                        />
                    </Box>
                ) : (
                    <Box className={classes.fields} automation-id="instance-detail-view">
                        <InfoField
                            label={intl.formatMessage(i18n.displayName)}
                            value={subscription.subscriptionName}
                            automationId="view-display-name"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.accountName)}
                            value={subscription.customerName}
                            automationId="view-customer-name"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.legalName)}
                            value={subscription.companyName}
                            automationId="view-company-name"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.salesforceId)}
                            value={subscription.salesforceLink}
                            action={
                                trimmed(subscription.salesforceLink) ? (
                                    <SalesforceLinkButton
                                        salesforceLink={subscription.salesforceLink}
                                    />
                                ) : undefined
                            }
                            automationId="view-salesforce-link"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.capacityGroupName)}
                            value={subscription.capacityGroupName}
                            automationId="view-capacity-group-name"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.capacityGroupId)}
                            value={subscription.capacityGroupId}
                            action={
                                subscription.capacityGroupId?.trim() ? (
                                    <PdsLinkButton capacityGroupId={subscription.capacityGroupId} />
                                ) : undefined
                            }
                            automationId="view-capacity-group-id"
                        />
                        <InfoField
                            label={intl.formatMessage(i18n.comment)}
                            value={subscription.comment}
                            automationId="view-comment"
                        />
                    </Box>
                )}
            </DetailSection>
            <ConfirmDiscardDialog
                open={editState.isConfirmingDiscard}
                title={intl.formatMessage(i18n.discardChangesTitle)}
                message={intl.formatMessage(i18n.discardChangesMessage)}
                discardLabel={intl.formatMessage(i18n.discardChangesConfirm)}
                keepEditingLabel={intl.formatMessage(i18n.discardChangesKeep)}
                onDiscard={handleConfirmDiscard}
                onKeepEditing={editState.cancelDiscard}
            />
        </>
    );
};
